import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Icon, Stack, Typography, useTheme } from '@mui/material';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { reduxHelper } from '../../../config/reduxHelper';
import TextPropertiesPanel from '../../siteBuilder/properties/TextPropertiesPanel';
import EkhoolImage from '../../ekhoolImage/ekhoolImage';
import ImageGallery from '../../themebuilder/ImageGallery';

const FlexibleContentBlock = ({ block, isSiteBuilder, isActive, handleUpdate, pageFontSx, onDelete, onDuplicate }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { push } = useRouter();
    const { siteBuilderSelectedChild, pageJson } = useSelector((s) => s.reducer);
    const [isHovered, setIsHovered] = useState(false);

    const fontFamily = pageJson?.pageStyle?.fontFamily || 'Roboto';

    useEffect(() => {
        if (isSiteBuilder && isActive && siteBuilderSelectedChild?.uid !== block.uid) {
            dispatch({
                type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD,
                payload: { ...block },
            });
        }
    }, [isActive, isSiteBuilder, block, siteBuilderSelectedChild, dispatch]);

    const handleClick = (e) => {
        if (isSiteBuilder) {
            e.stopPropagation();
            // Ensure selection sync on click
            if (siteBuilderSelectedChild?.uid !== block.uid) {
                dispatch({
                    type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD,
                    payload: { ...block },
                });
            }
            return;
        }

        if (block.url) {
            if (typeof block.url === 'string') {
                if (block.url.startsWith('http')) {
                    window.open(block.url, '_blank', 'noopener,noreferrer');
                } else {
                    push(block.url);
                }
            } else if (typeof block.url === 'object' && block.url.path) {
                if (block.url.target) {
                    window.open(block.url.path, '_blank', 'noopener,noreferrer');
                } else {
                    push(block.url.path);
                }
            }
        }
    };

    const { content } = block;
    if (!content) return null;

    const { title, icon, src } = content;

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                height: '100%'
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {isSiteBuilder && isActive && (
                <Box
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 10000,
                        mb: 1
                    }}
                >
                    <TextPropertiesPanel
                        childOverride={block}
                        onChangeChild={(updated) => handleUpdate(updated)}
                        onDeleteChild={() => onDelete && onDelete(block.id)}
                        onDuplicateChild={() => onDuplicate && onDuplicate(block.id)}
                    />
                </Box>
            )}

            <Card
                onClick={handleClick}
                sx={{
                    width: '100%',
                    height: '100%',
                    cursor: isSiteBuilder ? 'default' : (block.url ? 'pointer' : 'default'),
                    ...block.props?.sx,
                    ...block.props?.style,
                }}
            >
                <CardContent
                    sx={{
                        p: 2.5,
                        '&:last-child': { pb: 2.5 },
                        ...block.props?.sx
                    }}
                >
                    {block.variant === '1' && (
                        /* Variant 1: Matching customContent.js (Icon + Text Vertical) */
                        <Stack spacing={1}>
                            <Box>
                                <Icon
                                    fontSize={'medium'}
                                    color={'primary'}
                                    className={`mdi mdi-${icon || 'menu'}`}
                                    sx={{ height: '100%' }}
                                />
                            </Box>
                            <Typography
                                variant="h6"
                                contentEditable={isSiteBuilder}
                                suppressContentEditableWarning
                                onBlur={(e) => {
                                    const val = e.currentTarget.innerText;
                                    handleUpdate({
                                        ...block,
                                        content: { ...block.content, title: val }
                                    });
                                }}
                                onClick={(e) => isSiteBuilder && e.stopPropagation()}
                                sx={{
                                    fontWeight: block.props?.style?.fontWeight || 'bold',
                                    fontFamily: `${fontFamily}!important`,
                                    outline: 'none',
                                    color: 'text.primary',
                                    cursor: isSiteBuilder ? 'text' : 'inherit',
                                    ...block.props?.style,
                                }}
                            >
                                {title || (isSiteBuilder ? 'Content Title' : '')}
                            </Typography>
                            <Typography
                                contentEditable={isSiteBuilder}
                                suppressContentEditableWarning
                                onBlur={(e) => {
                                    const val = e.currentTarget.innerText;
                                    handleUpdate({
                                        ...block,
                                        content: { ...block.content, content: val }
                                    });
                                }}
                                onClick={(e) => isSiteBuilder && e.stopPropagation()}
                                variant="body2"
                                sx={{
                                    color: 'text.primary',
                                    fontFamily: `${fontFamily}!important`,
                                    outline: 'none',
                                    cursor: isSiteBuilder ? 'text' : 'inherit',
                                    ...block.props?.style2,
                                }}
                            >
                                {block.content?.content || (isSiteBuilder ? 'Content body text...' : '')}
                            </Typography>
                        </Stack>
                    )}

                    {block.variant === '2' && (
                        /* Variant 2: Matching customContent2.js (Image + Icon + Text Vertical) */
                        <Stack spacing={2}>
                            <Box sx={{ position: 'relative' }}>
                                {src ? (
                                    <Box
                                        position={'relative'}
                                        overflow={'hidden'}
                                        display={'flex'}
                                        justifyContent={'center'}
                                        width={'100%'}
                                        height={200}
                                        borderRadius="4px"
                                    >
                                        <EkhoolImage layout={'fill'} objectFit={'cover'} src={src} />
                                    </Box>
                                ) : (
                                    isSiteBuilder && (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: 200,
                                                bgcolor: 'action.hover',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '1px dashed grey',
                                                borderRadius: '4px'
                                            }}
                                        >
                                            <Icon color="disabled" fontSize="large">image</Icon>
                                        </Box>
                                    )
                                )}

                                {isSiteBuilder && isHovered && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: 'rgba(255,255,255,0.7)',
                                            zIndex: 2,
                                            borderRadius: '4px'
                                        }}
                                    >
                                        <ImageGallery
                                            onAddImage={(img) => {
                                                handleUpdate({
                                                    ...block,
                                                    content: { ...block.content, src: img }
                                                });
                                            }}
                                        >
                                            <Icon fontSize="large" sx={{ cursor: 'pointer', color: 'primary.main' }}>image</Icon>
                                        </ImageGallery>
                                    </Box>
                                )}
                            </Box>

                            <Stack spacing={1}>
                                {icon && (
                                    <Box>
                                        <Icon
                                            fontSize={'medium'}
                                            color={'primary'}
                                            className={`mdi mdi-${icon}`}
                                            sx={{ height: '100%' }}
                                        />
                                    </Box>
                                )}
                                <Typography
                                    variant="h6"
                                    contentEditable={isSiteBuilder}
                                    suppressContentEditableWarning
                                    onBlur={(e) => {
                                        const val = e.currentTarget.innerText;
                                        handleUpdate({
                                            ...block,
                                            content: { ...block.content, title: val }
                                        });
                                    }}
                                    onClick={(e) => isSiteBuilder && e.stopPropagation()}
                                    sx={{
                                        fontWeight: block.props?.style?.fontWeight || 'bold',
                                        fontFamily: `${fontFamily}!important`,
                                        outline: 'none',
                                        color: 'text.primary',
                                        cursor: isSiteBuilder ? 'text' : 'inherit',
                                        ...block.props?.style,
                                    }}
                                >
                                    {title || (isSiteBuilder ? 'Content Title' : '')}
                                </Typography>
                                <Typography
                                    contentEditable={isSiteBuilder}
                                    suppressContentEditableWarning
                                    onBlur={(e) => {
                                        const val = e.currentTarget.innerText;
                                        handleUpdate({
                                            ...block,
                                            content: { ...block.content, content: val }
                                        });
                                    }}
                                    onClick={(e) => isSiteBuilder && e.stopPropagation()}
                                    variant="body2"
                                    sx={{
                                        color: 'text.primary',
                                        fontFamily: `${fontFamily}!important`,
                                        outline: 'none',
                                        cursor: isSiteBuilder ? 'text' : 'inherit',
                                        ...block.props?.style2,
                                    }}
                                >
                                    {block.content?.content || (isSiteBuilder ? 'Content body text...' : '')}
                                </Typography>
                            </Stack>
                        </Stack>
                    )}

                    {block.variant === '3' && (
                        /* Variant 3: Matching customContent3.js (Horizontal with Chevron) */
                        <Stack
                            direction={"row"}
                            justifyContent={"space-between"}
                            alignItems={"center"}
                            width={"100%"}
                            sx={{ gap: 2 }}
                        >
                            <Stack direction={"row"} gap={2} alignItems={"center"} sx={{ flexGrow: 1 }}>
                                {(src || icon || isSiteBuilder) && (
                                    <Box sx={{ position: 'relative' }}>
                                        {src ? (
                                            <Box
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    position: 'relative',
                                                    borderRadius: '4px',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                <EkhoolImage layout={'fill'} objectFit={'cover'} src={src} />
                                            </Box>
                                        ) : (
                                            icon && (
                                                <Icon
                                                    fontSize={"medium"}
                                                    className={`mdi mdi-${icon}`}
                                                    sx={{ height: "100%", color: block.props?.style?.color || 'primary.main' }}
                                                />
                                            )
                                        )}

                                        {isSiteBuilder && isHovered && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: 'rgba(255,255,255,0.7)',
                                                    zIndex: 2,
                                                    borderRadius: '4px'
                                                }}
                                            >
                                                <ImageGallery
                                                    onAddImage={(img) => {
                                                        handleUpdate({
                                                            ...block,
                                                            content: { ...block.content, src: img }
                                                        });
                                                    }}
                                                >
                                                    <Icon fontSize="small" sx={{ cursor: 'pointer', color: 'primary.main' }}>image</Icon>
                                                </ImageGallery>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                <Stack direction={"column"} spacing={0.5} sx={{ flexGrow: 1 }}>
                                    <Typography
                                        contentEditable={isSiteBuilder}
                                        suppressContentEditableWarning
                                        onBlur={(e) => {
                                            const val = e.currentTarget.innerText;
                                            handleUpdate({
                                                ...block,
                                                content: { ...block.content, title: val }
                                            });
                                        }}
                                        onClick={(e) => isSiteBuilder && e.stopPropagation()}
                                        variant="h6"
                                        color={"text.primary"}
                                        sx={{
                                            fontWeight: 700,
                                            fontFamily: `${fontFamily}!important`,
                                            outline: 'none',
                                            cursor: isSiteBuilder ? 'text' : 'inherit',
                                            ...block.props?.style,
                                        }}
                                    >
                                        {title || (isSiteBuilder ? 'Content Title' : '')}
                                    </Typography>
                                    <Typography
                                        contentEditable={isSiteBuilder}
                                        suppressContentEditableWarning
                                        onBlur={(e) => {
                                            const val = e.currentTarget.innerText;
                                            handleUpdate({
                                                ...block,
                                                content: { ...block.content, content: val }
                                            });
                                        }}
                                        onClick={(e) => isSiteBuilder && e.stopPropagation()}
                                        variant={"body2"}
                                        color={"text.primary"}
                                        sx={{
                                            fontFamily: `${fontFamily}!important`,
                                            outline: 'none',
                                            cursor: isSiteBuilder ? 'text' : 'inherit',
                                            ...block.props?.style2,
                                        }}
                                    >
                                        {block.content?.content || (isSiteBuilder ? 'Content body text...' : '')}
                                    </Typography>
                                </Stack>
                            </Stack>

                            {block.url && (
                                <Box sx={{ flexShrink: 0 }}>
                                    <Icon className="mdi mdi-chevron-right" sx={{ fontSize: '2rem', color: 'text.secondary' }} />
                                </Box>
                            )}
                        </Stack>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default FlexibleContentBlock;
